import { defineStore } from "pinia";
import { fetchWrapper } from "../_helpers/fetch-wrapper";
import { useAuthStore } from "./auth.store";
export const useBettorStore = defineStore({
  id: "bettor",
  state: () => ({
    isLoading: false,
    isPotentialLoading: false,
    isConfirmedLoading: false,
    isResolvedLoading: false,
    isArchiveLoading: false,
    isMatchesInGroupLoading: false,
    items: [],
    allMatches: [],
    potentialMatches: [],
    potentialMatchesUngrouped: [],
    completedMatches: [],
    completedMatchesUngrouped: [],
    resolvedMatches: [],
    resolvedMatchesUngrouped: [],
    archiveMatches: [],
    archiveMatchesUngrouped: [],
    totalMatches: 0,
    totalPotentialMatches: 0,
    totalConfirmedMatches: 0,
    totalResolvedMatches: 0,
    totalArchiveMatches: 0,
    totalMatchesInGroup: 0,
    item: {},
    matchesInGroupItems: [],
    jurisdiction: [],
    leagues: [],
    contactInfo: {},
    notifications: {},
    // Shared-notes state (LP side mirrors LC).
    snNotes: [],
    snConversation: null,
    snLastReadAt: null,
    snNotesLoading: false,
    snServiceReachable: true,
    snIsActive: false,
    snEnrollmentId: null,
    snConfigured: false,
    snGroupStatus: {},
    applicationStatus: false,
    appStatusCause: "",
    getPotentialPagePagination: {
      page: 1,
      rowsPerPage: 25,
      search: "",
      sortBy: "when_match",
      sortType: "desc",
    },
    getConfirmedPagePagination: {
      page: 1,
      rowsPerPage: 25,
      search: "",
      sortBy: "when_match",
      sortType: "desc",
    },
    getResolvedPagePagination: {
      page: 1,
      rowsPerPage: 25,
      search: "",
      sortBy: "when_match",
      sortType: "desc",
    },
    getArchivedPagePagination: {
      page: 1,
      rowsPerPage: 25,
      search: "",
      sortBy: "when_match",
      sortType: "desc",
    },
    getMatchesInGroupPagePagination: {
      page: 1,
      rowsPerPage: 25,
      search: "",
      sortBy: "when_match",
      sortType: "desc",
    },
  }),
  actions: {
    // Shared-notes actions (LP side).
    async syncSharedNotes(groupHashes) {
      if (!groupHashes || groupHashes.length === 0) return;
      const uniq = Array.from(new Set(groupHashes.filter(Boolean)));
      if (uniq.length === 0) return;
      try {
        const res = await fetchWrapper.post("shared-notes/sync", { groups: uniq });
        if (res && !res.error) {
          this.snConfigured = true;
          await Promise.all(uniq.map((gh) => this._refreshGroupStatus(gh)));
        }
      } catch (_) {}
    },
    async _refreshGroupStatus(groupHash) {
      if (!groupHash) return;
      try {
        const [notesRes, convRes] = await Promise.all([
          fetchWrapper.get(
            "shared-notes/notes?group_hash=" + encodeURIComponent(groupHash)
          ),
          fetchWrapper.get("shared-notes/conversation/" + encodeURIComponent(groupHash)),
        ]);
        if (notesRes && !notesRes.error) {
          const notes = notesRes.notes || [];
          const liveNotes = notes.filter((n) => !n.when_deleted);
          let lastAutoAction = null;
          for (const n of liveNotes) {
            if (n.note_type === "auto_message" && n.content && n.content.operator_action) {
              lastAutoAction = n.content.operator_action;
            }
          }
          const hasNew = convRes && !convRes.error && convRes.conversation
            ? !!convRes.conversation.has_new
            : false;
          const noteCountByMatch = {};
          for (const n of liveNotes) {
            const mh = n.match_hash;
            if (mh) noteCountByMatch[mh] = (noteCountByMatch[mh] || 0) + 1;
          }
          this.snGroupStatus = {
            ...this.snGroupStatus,
            [groupHash]: { noteCount: liveNotes.length, noteCountByMatch, hasNew, lastAutoAction },
          };
        }
      } catch (_) {}
    },
    async fetchLocalNoteThread(groupHash) {
      if (!groupHash) return;
      this.clearSharedNotesState();
      this.snNotesLoading = true;
      try {
        const convRes = await fetchWrapper.get(
          "shared-notes/conversation/" + encodeURIComponent(groupHash)
        );
        if (convRes && !convRes.error && convRes.conversation) {
          this.snConversation = convRes.conversation;
          this.snLastReadAt = convRes.conversation.last_read || null;
        }
        const notesRes = await fetchWrapper.get(
          "shared-notes/notes?group_hash=" + encodeURIComponent(groupHash) + "&mark_read=true"
        );
        if (notesRes && !notesRes.error) this.snNotes = notesRes.notes || [];
      } catch (_) {
      } finally {
        this.snNotesLoading = false;
      }
    },
    clearSharedNotesState() {
      this.snNotes = [];
      this.snConversation = null;
      this.snLastReadAt = null;
      this.snNotesLoading = false;
    },
    async postNote({ matchHash, groupHash, noteType, content }) {
      if (!matchHash || !groupHash || !noteType || !content) return null;
      try {
        const res = await fetchWrapper.post("shared-notes/notes", {
          match_hash: matchHash, group_hash: groupHash, note_type: noteType, content,
        });
        if (res && !res.error) {
          await this.fetchLocalNoteThread(groupHash);
          return res.note || res;
        }
      } catch (_) {}
      return null;
    },
    async deleteNote(snsNoteId, groupHash) {
      if (!snsNoteId) return false;
      try {
        const res = await fetchWrapper.delete("shared-notes/notes/" + snsNoteId);
        if (res && !res.error) {
          if (groupHash) await this.fetchLocalNoteThread(groupHash);
          return true;
        }
      } catch (_) {}
      return false;
    },
    async ensureEnrollmentId(forceRefresh = false) {
      if (this.snIsActive && !forceRefresh) return;
      try {
        const res = await fetchWrapper.get("shared-notes/enrollment/status");
        if (res && !res.error && (res.is_active || res.enrollment_id)) {
          if (res.enrollment_id) this.snEnrollmentId = res.enrollment_id;
          this.snServiceReachable = true;
          this.snIsActive = !!res.is_active;
        } else if (res && !res.error) {
          this.snServiceReachable = true;
          this.snIsActive = false;
        } else {
          this.snServiceReachable = false;
          this.snIsActive = false;
        }
      } catch (e) {
        this.snServiceReachable = false;
        this.snIsActive = false;
      }
    },
    async resetSpecificObject() {
      this.getPotentialPagePagination = {
        page: 1,
        rowsPerPage: 25,
        search: "",
        sortBy: "when_match",
        sortType: "desc",
      };
      this.getConfirmedPagePagination = {
        page: 1,
        rowsPerPage: 25,
        search: "",
        sortBy: "when_match",
        sortType: "desc",
      };
      this.getResolvedPagePagination = {
        page: 1,
        rowsPerPage: 25,
        search: "",
        sortBy: "when_match",
        sortType: "desc",
      };
      this.getArchivedPagePagination = {
        page: 1,
        rowsPerPage: 25,
        search: "",
        sortBy: "when_match",
        sortType: "desc",
      };
      this.getMatchesInGroupPagePagination = {
        page: 1,
        rowsPerPage: 25,
        search: "",
        sortBy: "when_match",
        sortType: "desc",
      };
    },
    async updateUserRoleManually(newRole) {
      await fetchWrapper.post("update-user-role", { user_role: newRole }).then((res) => {
        if(res){
          const authStore = useAuthStore();
          authStore.updateRoleInStore(newRole);
        }
      });
    },
    async getApplicationStatus() {
      await fetchWrapper
        .get("get-application-status")
        .then((res) => {
          if (res.status) {
            this.applicationStatus = true;
            this.appStatusCause = res.app_status_cause;
          } else if (this.applicationStatus && !res.status) {
            const authStore = useAuthStore();
            this.applicationStatus = false;
            $(".modal").modal("hide");
            authStore.logout();
          }
        })
        .catch((error) => {
          console.error("Error fetching application status:", error);
        });
    },
    async getAll() {
      this.isLoading = true;
      await fetchWrapper.get("get-checks").then((res) => {
        if (res) {
          this.items = res.get_checks;
        }
      });

      this.isLoading = false;
    },
    async getMatches(uuid) {
      this.isLoading = true;
      await fetchWrapper.get("v2/get-matches?uuid=" + uuid).then((res) => {
        if (res) {
          this.allMatches = res.match_records;
          this.totalMatches = res.total_records;
        }
      });

      this.isLoading = false;
    },
    async getPotentialIncidents() {
      this.isPotentialLoading = true;
      await fetchWrapper
        .get(
          "v2/get-unresolved-matches?unresolved_status=U&page=" +
            this.getPotentialPagePagination.page +
            "&limit=" +
            this.getPotentialPagePagination.rowsPerPage +
            "&search=" +
            this.getPotentialPagePagination.search +
            "&sort_by=" +
            this.getPotentialPagePagination.sortBy +
            "&order_by=" +
            this.getPotentialPagePagination.sortType
        )
        .then((res) => {
          if (res) {
            this.potentialMatches = organizeRecords(
              res.result_unresolved_matches
            );
            this.totalPotentialMatches = res.total_records;
          }
        });
      this.isPotentialLoading = false;
    },
    async getPotentialIncidentsUngrouped() {
      this.isPotentialLoading = true;
      await fetchWrapper
        .get(
          "v2/get-unresolved-matches-ungrouped?unresolved_status=U&page=" +
            this.getPotentialPagePagination.page +
            "&limit=" +
            this.getPotentialPagePagination.rowsPerPage +
            "&search=" +
            this.getPotentialPagePagination.search +
            "&sort_by=" +
            this.getPotentialPagePagination.sortBy +
            "&order_by=" +
            this.getPotentialPagePagination.sortType
        )
        .then((res) => {
          if (res) {
            this.potentialMatchesUngrouped = organizeRecordsUngrouped(
              res.result_unresolved_matches
            );
            this.totalPotentialMatches = res.total_records;
          }
        });
      this.isPotentialLoading = false;
    },
    async getCompletedIncidents() {
      this.isConfirmedLoading = true;
      await fetchWrapper
        .get(
          "v2/get-unresolved-matches?unresolved_status=C&page=" +
            this.getConfirmedPagePagination.page +
            "&limit=" +
            this.getConfirmedPagePagination.rowsPerPage +
            "&search=" +
            this.getConfirmedPagePagination.search +
            "&sort_by=" +
            this.getConfirmedPagePagination.sortBy +
            "&order_by=" +
            this.getConfirmedPagePagination.sortType
        )
        .then((res) => {
          if (res) {
            this.completedMatches = organizeRecords(
              res.result_unresolved_matches
            );
            this.totalConfirmedMatches = res.total_records;
          }
        });
      this.isConfirmedLoading = false;
    },
    async getCompletedIncidentsUngrouped() {
      this.isConfirmedLoading = true;
      await fetchWrapper
        .get(
          "v2/get-unresolved-matches-ungrouped?unresolved_status=C&page=" +
            this.getConfirmedPagePagination.page +
            "&limit=" +
            this.getConfirmedPagePagination.rowsPerPage +
            "&search=" +
            this.getConfirmedPagePagination.search +
            "&sort_by=" +
            this.getConfirmedPagePagination.sortBy +
            "&order_by=" +
            this.getConfirmedPagePagination.sortType
        )
        .then((res) => {
          if (res) {
            this.completedMatchesUngrouped = organizeRecordsUngrouped(
              res.result_unresolved_matches
            );
            this.totalConfirmedMatches = res.total_records;
          }
        });
      this.isConfirmedLoading = false;
    },
    async getResolvedIncidents() {
      this.isResolvedLoading = true;
      await fetchWrapper
        .get(
          "v2/get-resolved-matches?page=" +
            this.getResolvedPagePagination.page +
            "&limit=" +
            this.getResolvedPagePagination.rowsPerPage +
            "&search=" +
            this.getResolvedPagePagination.search +
            "&sort_by=" +
            this.getResolvedPagePagination.sortBy +
            "&order_by=" +
            this.getResolvedPagePagination.sortType
        )
        .then((res) => {
          if (res) {
            this.resolvedMatches = organizeRecords(res.result_resolved_matches);
            this.totalResolvedMatches = res.total_records;
          }
        });
      this.isResolvedLoading = false;
    },
    async getResolvedIncidentsUngrouped() {
      this.isResolvedLoading = true;
      await fetchWrapper
        .get(
          "v2/get-resolved-matches-ungrouped?page=" +
            this.getResolvedPagePagination.page +
            "&limit=" +
            this.getResolvedPagePagination.rowsPerPage +
            "&search=" +
            this.getResolvedPagePagination.search +
            "&sort_by=" +
            this.getResolvedPagePagination.sortBy +
            "&order_by=" +
            this.getResolvedPagePagination.sortType
        )
        .then((res) => {
          if (res) {
            this.resolvedMatchesUngrouped = organizeRecordsUngrouped(res.result_resolved_matches);
            this.totalResolvedMatches = res.total_records;
          }
        });
      this.isResolvedLoading = false;
    },
    async getArchiveIncidents() {
      this.isArchiveLoading = true;
      await fetchWrapper
        .get(
          "v2/get-no-match-matches?page=" +
            this.getArchivedPagePagination.page +
            "&limit=" +
            this.getArchivedPagePagination.rowsPerPage +
            "&search=" +
            this.getArchivedPagePagination.search +
            "&sort_by=" +
            this.getArchivedPagePagination.sortBy +
            "&order_by=" +
            this.getArchivedPagePagination.sortType
        )
        .then((res) => {
          if (res) {
            this.archiveMatches = organizeRecords(res.result_no_match_matches);
            this.totalArchiveMatches = res.total_records;
          }
        });
      this.isArchiveLoading = false;
    },
    async getArchiveIncidentsUngrouped() {
      this.isArchiveLoading = true;
      await fetchWrapper
        .get(
          "v2/get-no-match-matches-ungrouped?page=" +
            this.getArchivedPagePagination.page +
            "&limit=" +
            this.getArchivedPagePagination.rowsPerPage +
            "&search=" +
            this.getArchivedPagePagination.search +
            "&sort_by=" +
            this.getArchivedPagePagination.sortBy +
            "&order_by=" +
            this.getArchivedPagePagination.sortType
        )
        .then((res) => {
          if (res) {
            this.archiveMatchesUngrouped = organizeRecordsUngrouped(res.result_no_match_matches);
            this.totalArchiveMatches = res.total_records;
          }
        });
      this.isArchiveLoading = false;
    },
    async getMatch(id) {
      this.isLoading = true;
      await fetchWrapper.post("v2/get-match", { id: id }).then((res) => {
        if (res) {
          this.item = res.match_record;
        }
      });
      this.isLoading = false;
    },
    async getMatchesInGroup(groupId, matchStatus) {
      this.isMatchesInGroupLoading = true;
      await fetchWrapper
      .get(
        "v2/get-matches-in-group?group_id=" +
          groupId +
          "&match_status=" +
          matchStatus +
          "&page=" +
          this.getMatchesInGroupPagePagination.page +
          "&limit=" +
          this.getMatchesInGroupPagePagination.rowsPerPage +
          "&search=" +
          this.getMatchesInGroupPagePagination.search +
          "&sort_by=" +
          this.getMatchesInGroupPagePagination.sortBy +
          "&order_by=" +
          this.getMatchesInGroupPagePagination.sortType
      )
      .then((res) => {
        if (res) {
          this.matchesInGroupItems = res.match_records;
          this.totalMatchesInGroup = res.total_records;
        }
      });
      this.isMatchesInGroupLoading = false;
    },
    async updateNotStatus(ids) {
      this.isLoading = true;
      await fetchWrapper
        .post("update-notification-status", { ids: ids })
        .then((res) => {
          if (res) {
            this.getNotifications();
          }
        });
      this.isLoading = false;
    },
    async updateUnreadStatus(ids) {
      this.isLoading = true;
      await fetchWrapper
        .post("update-unread-notification-status", { ids: ids })
        .then((res) => {
          if (res) {
            this.getNotifications();
          }
        });
      this.isLoading = false;
    },
    async getContactInfo(unique_id) {
      this.isLoading = true;
      await fetchWrapper
        .post("matches-get-contact-info", { unique_id: unique_id })
        .then((res) => {
          if (res) {
            this.contactInfo = res.match_contact_message;
          }
        });
      this.isLoading = false;
    },
    async getNotifications() {
      await fetchWrapper.get("get-notifications").then((res) => {
        if (res) {
          this.notifications = res.notification_count;
        }
      });
    },
    async getJurisdiction(countries) { 
      let url = "v2/get-jurisdiction"; 
      if (Array.isArray(countries) && countries.length > 0) {
        const query = countries
          .map((c) => `country=${encodeURIComponent(c)}`)
          .join("&");
        url += `?${query}`;
      }
    
      await fetchWrapper.get(url).then((res) => {
        if (res) {
          this.jurisdiction = res.data;
        }
      });
    },
    async getLeagues() {
      const response = await fetchWrapper.get("get-leagues");
      if (response) {
        const responseData = response.data;
        responseData.sort((a, b) =>
          a.display_name.localeCompare(b.display_name)
        );
        this.leagues = [];
        for (const element of responseData) {
          this.leagues.push({
            id: element.id,
            level: element.level,
            parent_id: element.parent_id,
            value: element.display_name,
            name: element.name,
            display: element.display_name,
          });
        }
      }
    },
    async updateMatchStatus(id, match_status, match_status_notes) {
      await fetchWrapper
        .post("update-match-status", {
          id: parseInt(id),
          match_status: match_status,
          match_status_notes: match_status_notes,
        })
        .then(() => {
          // this.item = res.result_match;
        });
    },
    async updatePassword(current_password, password, confirm_password) {
      return await fetchWrapper.post("update-user-password", {
        current_password: current_password,
        password: password,
        confirm_password: confirm_password,
      });
    },
    async updateMatchStatusSilence(id, silent_mode) {
      await fetchWrapper.post("match/update-status-silenced", {
        id: parseInt(id),
        silent_mode: silent_mode,
      });
    },
    async updateCSVCheck() {
      await fetchWrapper.post("update-csv-acceptance", {
        csv_acceptance: true,
      });
      const authStore = useAuthStore();
      authStore.csvAccept = 1;
      localStorage.setItem("csvAccept", 1);
    },
  },
});
const organizeRecords = (records) => {
  const result = [];
  const recordMap = [];
  records.forEach((record) => {
    record.showChildren = false;
    const { group_id, ...rest } = record;
    if (typeof group_id !== "undefined") {
      if (result.some((record) => record.group_id === group_id)) {
        recordMap.push({ group_id, ...rest }); // Add record to recordMap
      } else {
        result.push({ group_id, ...rest, childrens: [] }); // Add showChildren property
      }
    }
  });
  result.forEach((parent) => {
    if (typeof parent.group_id !== "undefined") {
      parent.childrens = recordMap.filter(
        (record) => record.group_id === parent.group_id
      );
      parent.childrens.sort(
        (a, b) => new Date(b.when_match) - new Date(a.when_match)
      ); // Sort childrens array in descending order based on when_match
    }
  });
  console.log(result, "organize result");
  return result;
};
const organizeRecordsUngrouped = (records) => {
  return records.map(({ match, group_total_records }) => ({
    ...match,
    group_total_records,
  }));
};
